import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppState } from '../app.service';
import { SearchService, ResourceTypeService, SeoService, SortOptionService, EventLoggingService } from '../_services';
import { SliderModel } from '../_interfaces';
import { QueryString } from '../_utilities/querystring';
import { EventLogAttributes } from '../_interfaces/event-log/eventlog.model';
import { ProductCheck } from '../_utilities';


declare var dataLayer: any;

@Component({
  selector: 'pq-results',
  templateUrl: './results.component.html',
  styleUrls: ['./_results.component.scss']
})
export class ResultsComponent implements OnDestroy {
  public title: string = 'Results';
  public hasResultsForSourceType: boolean = false;
  public hasResults: boolean = false;
  public page: number = 1;
  public term: string;
  public sourceTypes: object[] = [];
  public limitTo: object[] = [];
  public results: object[] = [];
  public isImageResults: boolean = false;
  public hasSpellSuggestions: boolean = false;
  public isCurrFilterApplied: boolean = true;
  public spellSuggestions: string = '';
  public additionalMessage: string = '';
  public imageRow: ImageRow;
  public suppressSourceTypes: boolean = false;
  public suppressLimitTo: boolean = false;

  public relatedSearches: object[] = [];
  public relatedSearchTitle: string = '';

  public topResearchTopic: object = {};

  public displayTotal: string;
  public total: number;

  public relatedTopicsData: SliderModel =  new SliderModel('carousel-1', 'Related Topics', []);

  public imageSearchResults: object[] = [];
  public numberPerPage: string = this._search.getResultsToShow();
  public queryParams: Object = {};
  public profile: string;
  public resultsUrl: string = '/results';
  public error: boolean = false;
  public loading: boolean = true;
  public msg: string = this.appState.get('resultsGenericError');

  private _subscription: Subscription;

  constructor (
    public  appState: AppState,
    private _queryString: QueryString,
    private _route: ActivatedRoute,
    private _search: SearchService,
    private _sourceTypeSvc: ResourceTypeService,
    private _sortOptionSvc: SortOptionService,
    private _seoSvc: SeoService,
    private _eventLogSvc: EventLoggingService,
    private _productCheck: ProductCheck
  ) {

    this._seoSvc.setTitle(this.title);

    this._subscription = _route.queryParams.subscribe(
      (queryParam: any) => {
        let topic = (<any> this.topResearchTopic);

        this.queryParams = queryParam;
        this.page = queryParam['page'] ? parseInt(queryParam['page'], 0) : 1;
        this.term = queryParam['query'];
        this.loading = true;
        this.error = false;
        this.additionalMessage = '';
        this.spellSuggestions = '';
        this.hasSpellSuggestions = false;
        this.profile = _route.snapshot.params['profile'] ?  '/' + _route.snapshot.params['profile'] + '/' : '/';

        let currentProfile = this.appState.get('current_profile');
        this.relatedTopicsData.title = this._productCheck.iseLibraryCheck(currentProfile) ? 'Related Topics' : this._productCheck.isResearcher(currentProfile) ? 'Related Leading Issues' : 'Related Research Topics';
        // display error in ui if there is no search term found
        if (!this.term) {
          this.error = true;
        }

        this.relatedSearchTitle = this.appState.get('resultsRelatedPartial') + ' "' + this.term + '"';

        this.resultsUrl = this.profile + 'results';

        // get profile settings from appState(from ui profile service  based on profile in url or using default
        // if not found in url) for changing the display
        let profileToken =  _route.snapshot.params['profile'] || 'default';

        let settings = (typeof this.appState.get('profile') !== 'undefined') && this.appState.get('profile')[profileToken] &&
          this.appState.get('profile')[profileToken]['settings'] ? this.appState.get('profile')[profileToken]['settings'] : [];

        if (settings.length >= 1) {
          settings = settings[0];
        }

        // get appState (from product service) settings for the number of results to display per page
        if (this.appState.get('DEFAULT_RESULTS_PER_PAGE')) {
          this.numberPerPage = String(this.appState.get('DEFAULT_RESULTS_PER_PAGE'));
          this._search.setResultsToShow(String(this.appState.get('DEFAULT_RESULTS_PER_PAGE')));
        }

        // get appState (from product service) settings for results default sort order
        if (this.appState.get('sortOrder') && this._sortOptionSvc.isSupportedSort(this.appState.get('sortOrder'))) {
          this._search.setSortOrder(this.appState.get('sortOrder'));
        } else {
          this._search.setSortOrder(this._sortOptionSvc.get(1)['value']);
        }

        // determine if results on page 1
        if (typeof queryParam['page'] !== 'undefined' && parseInt(queryParam['page'], 0) > 1) {
          this.appState.set({ logSearch: false });
        }

        // populate limit to filters from ui profile and update if the filters are suppressed
        if (settings['simpleLimiters']) {
          this.limitTo = settings['simpleLimiters']['simpleLimiter'] || [];
          this.limitTo = this.limitTo.sort((a, b) => {
            return parseInt(a['position']) - parseInt(b['position']);
          });

          this.suppressLimitTo = typeof settings['simpleLimiters']['suppress'] === 'undefined' ? true : settings['simpleLimiters']['suppress'];
        }

        if (settings['sourceTypes']) {
          this.sourceTypes = settings['sourceTypes']['sourceType'] || [];
          // remove all option for results view
          this.sourceTypes =  this.sourceTypes.filter((source) => {
            return source['key'].toLowerCase() !== 'all';
          });
          this.sourceTypes.forEach((source, index) => {
            source['id'] = index + 1;
            source['icon'] = 'gr-' + source['key'].toLowerCase();
            source['count'] = 0;
          });

          this.sourceTypes = this.sourceTypes.sort((a, b) => {
            return parseInt(a['position']) - parseInt(b['position']);
          });

          this._sourceTypeSvc.set(this.sourceTypes);

          this.suppressSourceTypes = typeof settings['sourceTypes']['suppress'] === 'undefined' ? true : settings['sourceTypes']['suppress'];
        }

        this._search.doSearch(queryParam).then((resp: Response) => {
          let data = resp;
          this.loading = false;
          this.error = false;

          if (queryParam['source'] && queryParam['source'].toLowerCase() === 'images') {
            this.isImageResults = true;
          } else {
            this.isImageResults = false;
          }

          this.topResearchTopic = data['GrappleSearchResponse']['researchTopicItem'] &&
                                    data['GrappleSearchResponse']['researchTopicItem']['searchResults'] ?
                                        data['GrappleSearchResponse']['researchTopicItem']['searchResults'] : {};

          this._search.results = data['GrappleSearchResponse'] || { searchResults: [], resultsCount: '0' };
          this.imageRow =  <ImageRow> data['GrappleSearchResponse']['imageRow'];

          this.hasResultsForSourceType = typeof this._search.results.resultsCountBySourceType !== 'undefined';

          this.isCurrFilterApplied = data['GrappleSearchResponse']['currencyFiltersApplied'] === 'true';

          if (typeof queryParam['currencyfilter'] !== 'undefined') {
            this.isCurrFilterApplied = queryParam['currencyfilter']  === 'true';
          }

          if (this.hasResultsForSourceType) {
            // check to see if results is not an array and if not put results into an array
            if (!Array.isArray(this._search.results.resultsCountBySourceType)) {
              this._search.results.resultsCountBySourceType = [this._search.results.resultsCountBySourceType];
            }

            if (this.page > 1 && this.appState.get('resultsCountBySourceType') !== null) {
              this._search.results.resultsCountBySourceType = this.appState.get('resultsCountBySourceType');
            }

            // update sourceType count by pulling it from the results
            this._search.results.resultsCountBySourceType.forEach((item) => {
              this._sourceTypeSvc.setCount(item['resultsCount'], item['sourceType']);
            });

            if (this.page === 1) {
              this.appState.set({ resultsCountBySourceType: this._search.results.resultsCountBySourceType });
            }

          }

          let previousQuery = this._queryString.getQueryString(document.referrer)['query'];
          // reset the related terms for new searches.
          if (this.term !== previousQuery) {
            this.relatedSearches = [];
          }

          if (this._search.results['RelatedSearchKeywords']
            && this._search.results['RelatedSearchKeywords']['searchKeyword']
          ) {

            let keywords = this._search.results['RelatedSearchKeywords']['searchKeyword'];
            if (!Array.isArray(keywords) && typeof keywords !== 'undefined') {
              keywords = [ keywords ];
            }

            if (Array.isArray(keywords)) {
              this.relatedSearches = this._formatKeywordCollection(keywords);
            }
          }

          if (this._search.results.suggestedSearchTerm !== 'undefined') {
            if (this._search.results.searchResubmittedFlag === 'true') {
              this.additionalMessage = this.appState.get('resultsNoResultsPartial') + ' "' + this.term + '".';
              this.term = this._search.results.suggestedSearchTerm;
            } else if (this._search.results.searchResubmittedFlag === 'false') {
              this.hasSpellSuggestions = true;
              this.spellSuggestions = this._search.results.suggestedSearchTerm;
            }
          }
          this.hasResults = typeof this._search.results.searchResults !== 'undefined'
            && parseInt(this._search.results.resultsCount, 0) > 0;

          //logging event for searchReuslts and count;
          const eventLogRequest = this._eventLogSvc.logEvent(this.buildEventAttributes());
          eventLogRequest.catch((err: any) => {
            // keep for testing reasons
            // console.log(err);
          });

          if (this.hasResults) {

            // check to see if results is not an array and if not put results into an array
            if (Object.prototype.toString.call(this._search.results.searchResults) !== '[object Array]') {
              this._search.results.searchResults = [this._search.results.searchResults];
            }

            // update results to include source type icon
            this._search.results.searchResults.forEach((item) => {
              (<any> item)['icon'] = this._sourceTypeSvc.getIcon((<any> item)['attributes'].sourceType);
            });

            // related topics row
            if (data['GrappleSearchResponse']['RelatedResearchTopicItems']
              && data['GrappleSearchResponse']['RelatedResearchTopicItems']['TopicTreeNode']) {
              if (!Array.isArray(data['GrappleSearchResponse']['RelatedResearchTopicItems']['TopicTreeNode'])) {
                data['GrappleSearchResponse']['RelatedResearchTopicItems']['TopicTreeNode'] = [data['GrappleSearchResponse']['RelatedResearchTopicItems']['TopicTreeNode']];
              }

              this.relatedTopicsData.slide = data['GrappleSearchResponse']['RelatedResearchTopicItems']['TopicTreeNode'];
            } else {
              this.relatedTopicsData.slide = [];
            }

            this.results = this._search.results.searchResults;
          } else {
            if (dataLayer && Array.isArray(dataLayer)) {
              dataLayer.push({'event': 'NoResultsFound'});
            }
          }

          this.displayTotal = this._search.results.resultsCount
            ? Number(this._search.results.resultsCount).toLocaleString() : '0';

          this.total = this._search.results.resultsCount
            ? parseInt(this._search.results.resultsCount, 0) : 0;
          this.appState.set({ advanceSearchType: false });
          this.appState.set({ basicSearchType: false });
        }).catch((error) => {
          let data = error;
          this.loading = false;
          this.error = true;
          if (data['errors']
          && data['errors']['statusCode']
          && data['errors']['statusCode'].toLowerCase().indexOf('400') !== -1) {

            if (data['errors']['error']['msg']) {
              this.msg = error['errors']['error']['msg'];
            } else {
              throw new Error(error.status);
            }
          } else {
            throw new Error(error.status);
          }
          this.appState.set({ advanceSearchType: false });
          this.appState.set({ basicSearchType: false });
        });
      }
    );

  }

  public ngOnDestroy () {
    this._subscription.unsubscribe();
  }

  private buildEventAttributes (): EventLogAttributes {
    let eventLogAttr: EventLogAttributes  = new EventLogAttributes();

    if (this.appState.get('EVENTTYPES') !== 'undefined' && this.appState.get('EVENTTYPES') != null ) {
      let eventTypes = this.appState.get('EVENTTYPES');

      for (let eType of eventTypes) {
        if (eType.type === 'SEARCH_QUERY') {
          eventLogAttr.eventType = eType.id;
        }
      }
    }
    eventLogAttr.eventParameter = 'search';
    eventLogAttr.message = 'Search query  [' + this.term + '] results count ['+this._search.results.resultsCount+']';
    return eventLogAttr;
  }

  private _formatKeywordCollection (keywords: string[]): object[] {
    let collection = [];
    let baseUri = this.profile !== '' ? this.profile + 'results' : '/results';

    keywords.forEach((keyword) => {

      if (keyword !== this.term) {
        collection.push({
          url: baseUri + '?query=' + keyword,
          name: keyword,
        });
      }
    });

    return collection;
  }
}
